import { useState } from "react";
import { API } from "../api";

// Chunk 16D — login flow has two phases when the user has TOTP MFA
// enabled: 1) submit username + password to /token, 2) if response carries
// requires_mfa, swap to a code-entry form and submit the 6-digit code +
// the challenge_token to /token/mfa.  Users without MFA enabled never see
// phase 2 — /token returns a real access_token directly.
export default function LoginPage({ onLogin }) {
  const [phase, setPhase] = useState("credentials");   // "credentials" | "mfa"
  const [form, setForm] = useState({ username: "", password: "" });
  const [code, setCode] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Shared post-success handler — runs after either /token (no MFA) or
  // /token/mfa (with MFA) issues a real access_token.
  const finalizeLogin = async (accessToken, user) => {
    localStorage.setItem("token", accessToken);
    localStorage.setItem("user", JSON.stringify(user));
    try {
      const permsRes = await fetch(API + "/me/permissions", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (permsRes.ok) {
        localStorage.setItem("permissions", JSON.stringify(await permsRes.json()));
      } else {
        localStorage.removeItem("permissions");
        console.warn("Failed to load /me/permissions — UI falling back to server-side gates.");
      }
    } catch (err) {
      localStorage.removeItem("permissions");
      console.warn("Failed to load /me/permissions:", err);
    }
    onLogin(user);
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const body = new URLSearchParams({ username: form.username, password: form.password });
      const res = await fetch(API + "/token", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      if (data.requires_mfa) {
        // Password verified — now ask for the second factor.  Don't store
        // the challenge in localStorage; a tab close should drop it.
        setMfaChallenge(data.mfa_challenge_token);
        setCode("");
        setPhase("mfa");
        setLoading(false);
        return;
      }
      await finalizeLogin(data.access_token, data.user);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await fetch(API + "/token/mfa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfa_challenge_token: mfaChallenge, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "MFA verification failed");
      await finalizeLogin(data.access_token, data.user);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    setPhase("credentials");
    setMfaChallenge(null);
    setCode("");
    setError("");
  };

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(135deg,#1a2744 0%,#2e3f6e 100%)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"white", borderRadius:10, boxShadow:"0 20px 60px rgba(0,0,0,0.4)", width:"100%", maxWidth:400, overflow:"hidden" }}>
        <div style={{ background:"linear-gradient(135deg,#1a2744,#2e3f6e)", padding:"32px", textAlign:"center" }}>
          <div style={{ color:"white", fontSize:22, fontWeight:800, letterSpacing:1, marginBottom:6 }}>WALTERS <span style={{color:"#7fb3ff"}}>NORTHWEST</span></div>
          <div style={{ color:"rgba(255,255,255,0.55)", fontSize:12 }}>Claims Management System</div>
        </div>

        {phase === "credentials" && (
          <form onSubmit={submitCredentials} style={{ padding:"28px 32px" }}>
            <div className="form-group" style={{ marginBottom:16 }}>
              <label>Username</label>
              <input value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="Enter username" autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom:24 }}>
              <label>Password</label>
              <input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="••••••••" />
            </div>
            {error && <div className="alert-error">{error}</div>}
            <button type="submit" className="btn btn-primary" style={{ width:"100%", justifyContent:"center", padding:"10px" }} disabled={loading}>
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        )}

        {phase === "mfa" && (
          <form onSubmit={submitMfa} style={{ padding:"28px 32px" }}>
            <div style={{ marginBottom:16, fontSize:13, color:"#475569" }}>
              Enter the 6-digit code from your authenticator app.
            </div>
            <div className="form-group" style={{ marginBottom:24 }}>
              <label>Authentication code</label>
              <input
                value={code}
                onChange={e=>setCode(e.target.value)}
                placeholder="123 456"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={10}
                style={{ letterSpacing:2, fontFamily:"JetBrains Mono,monospace", fontSize:18, textAlign:"center" }}
              />
            </div>
            {error && <div className="alert-error">{error}</div>}
            <button type="submit" className="btn btn-primary" style={{ width:"100%", justifyContent:"center", padding:"10px" }} disabled={loading || code.trim().length < 6}>
              {loading ? "Verifying..." : "Verify"}
            </button>
            <button type="button" onClick={startOver} disabled={loading} style={{ marginTop:10, background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:12, width:"100%", textAlign:"center", padding:"6px" }}>
              ← Start over
            </button>
          </form>
        )}

        <div style={{ padding:"0 32px 20px", textAlign:"center", fontSize:11, color:"#94a3b8" }}>
          <div>Walters Northwest Claims Services, Inc.</div>
          <div style={{ marginTop:6 }}>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color:"#64748b", textDecoration:"none" }}>Privacy Policy</a>
            <span style={{ margin:"0 8px" }}>·</span>
            <a href="/eula.html" target="_blank" rel="noopener noreferrer" style={{ color:"#64748b", textDecoration:"none" }}>Terms (EULA)</a>
          </div>
        </div>
      </div>
    </div>
  );
}
